import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PaEmptyState } from "components/platform-admin";
import {
  buildStripeCatalogChartData,
  buildSubscriptionStatusChartData,
  FINANCIAL_CHART_COLORS,
  formatCurrency,
} from "./billingOverview.utils";

const ChartCard = ({ title, description, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
    <div className="mt-4 h-72">{children}</div>
  </section>
);

const EmptyChart = ({ message }) => (
  <div className="flex h-full items-center justify-center">
    <PaEmptyState title="No chart data" message={message} />
  </div>
);

const renderPieLabel = ({ name, percent }) =>
  percent >= 0.08 ? `${name} ${(percent * 100).toFixed(0)}%` : "";

const StripeManagementCharts = ({ billing, plans = [] }) => {
  const currency = billing?.estimated_mrr?.currency || plans[0]?.currency || "AUD";

  const statusMix = useMemo(
    () => buildSubscriptionStatusChartData(billing?.subscription_status_breakdown),
    [billing]
  );
  const catalogPrices = useMemo(
    () =>
      buildStripeCatalogChartData(
        billing?.stripe_catalog_summary?.length
          ? billing.stripe_catalog_summary
          : plans.map((row) => ({
              product_name: row.product_name,
              monthly_price_cents: row.unit_amount,
              currency: row.currency,
            }))
      ),
    [billing, plans]
  );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <ChartCard
        title="Stripe subscription status"
        description="Distribution of Stripe-reported subscription statuses across tenants."
      >
        {statusMix.length === 0 ? (
          <EmptyChart message="No subscription status data." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={statusMix}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={95}
                label={renderPieLabel}
              >
                {statusMix.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={FINANCIAL_CHART_COLORS[index % FINANCIAL_CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [`${value} tenants`, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Catalog price ladder"
        description="Published Stripe product prices used for billing and MRR estimates."
      >
        {catalogPrices.length === 0 ? (
          <EmptyChart message="Stripe catalog unavailable or has no priced products." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={catalogPrices}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(value, currency)}
              />
              <YAxis type="category" dataKey="product" width={120} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name, props) => [
                  props?.payload?.monthly_price_label || formatCurrency(Number(value), currency),
                  "Price",
                ]}
              />
              <Bar dataKey="monthly_price_cents" fill="#6366f1" radius={[0, 4, 4, 0]} name="Price" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
};

export default StripeManagementCharts;
